/**
 * UserModal — 用户信息设置弹窗
 *
 * 触发时机：首次访问群聊页面且 localStorage 中无 userId 时
 * 功能：昵称输入、头像上传（base64 预览）、确认
 * 提交后存入 localStorage：昵称、头像(base64)、userId(UUID)
 */

import React, { useState, useCallback, useRef } from 'react';
import './index.scss';

export interface UserModalProps {
  /** 确认回调，返回用户信息 */
  onConfirm: (user: { nickname: string; avatar: string }) => void;
}

/**
 * 生成简易 UUID v4
 * @returns UUID 字符串
 */
const generateUUID = (): string => {
  const hex = '0123456789abcdef';
  const segments = [8, 4, 4, 4, 12];
  return segments
    .map((len) => {
      let s = '';
      for (let i = 0; i < len; i++) s += hex[Math.floor(Math.random() * 16)];
      return s;
    })
    .join('-');
};

/**
 * 用户信息设置弹窗
 */
const UserModal: React.FC<UserModalProps> = ({ onConfirm }) => {
  const [nickname, setNickname] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarBase64, setAvatarBase64] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** 头像上传 */
  const handleAvatarSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('头像大小不能超过 2MB');
      return;
    }

    setAvatarPreview(URL.createObjectURL(file));

    const reader = new FileReader();
    reader.onload = () => setAvatarBase64(reader.result as string);
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  /** 确认提交 */
  const handleConfirm = useCallback(() => {
    const trimmed = nickname.trim();
    if (!trimmed) {
      setError('请输入昵称');
      return;
    }
    if (trimmed.length > 20) {
      setError('昵称不能超过 20 个字符');
      return;
    }

    // 如果没有上传头像，使用默认头像（首字母）
    const avatar = avatarBase64 || '';

    // 将 userId 直接拼在回调数据中
    const userId = generateUUID();

    // 存 localStorage
    localStorage.setItem('group_chat_user', JSON.stringify({ userId, nickname: trimmed, avatar }));
    // 同时也存 userId 单独字段，方便读取
    localStorage.setItem('group_chat_user_id', userId);

    onConfirm({ nickname: trimmed, avatar });
  }, [nickname, avatarBase64, onConfirm]);

  return (
    <div className="user-modal-overlay">
      <div className="user-modal">
        <h2 className="user-modal-title">加入群聊</h2>
        <p className="user-modal-desc">设置你的昵称和头像，开始聊天</p>

        {/* 头像上传区域 */}
        <div className="user-modal-avatar-section">
          <div
            className="user-modal-avatar"
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            aria-label="上传头像"
          >
            {avatarPreview ? (
              <img src={avatarPreview} alt="头像预览" />
            ) : (
              <span className="user-modal-avatar-placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </span>
            )}
            <span className="user-modal-avatar-hint">点击上传头像</span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarSelect}
            style={{ display: 'none' }}
          />
        </div>

        {/* 昵称输入 */}
        <div className="user-modal-input-group">
          <label className="user-modal-label">昵称</label>
          <input
            className="user-modal-input"
            type="text"
            value={nickname}
            onChange={(e) => {
              setNickname(e.target.value);
              setError(null);
            }}
            placeholder="输入你的昵称"
            maxLength={20}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
          />
        </div>

        {/* 错误提示 */}
        {error && <p className="user-modal-error">{error}</p>}

        {/* 确认按钮 */}
        <button
          className="user-modal-confirm"
          onClick={handleConfirm}
          disabled={!nickname.trim()}
        >
          进入群聊
        </button>
      </div>
    </div>
  );
};

export default UserModal;
